import React, { useContext } from 'react';
import classes from './ScheduleBox.module.scss';
import { UseFormReturn } from 'react-hook-form';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { ButtonGroup } from '../../../../components/containers/ButtonGroup';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { PostTiming } from '../../../../models/graphql';
import { AddToQueue } from '../AddToQueue';
import { EvergreenRecycle } from '../EvergreenRecycle';
import { SchedulePost } from '../SchedulePost';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { EvergreenRecycleViewProps } from '../EvergreenRecycle/EvergreenRecycle.view';
import classNames from 'classnames';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { hasSeverity } from '../../validators/helpers/severity-checker';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { getCustomizationProfileId } from '../../validators/helpers/customization-getter';
import { useSettings } from '../../../../hooks/useSettings';
import { AppContext } from 'providers/AppProvider';
import { PostEditorContextEnum } from 'models/post-editor';
import { POST_EDITOR_ERROR_CLASS } from '../../../../constants/styleConstants';

export type ScheduleBoxViewProps = {
  //
  formInstance: UseFormReturn;
  borderless?: boolean;
  postId?: string;
};

export const ScheduleBoxView = ({ formInstance, borderless, postId }: ScheduleBoxViewProps): JSX.Element => {
  const { state: postEditorState, dispatch } = useContext(PostEditorContext);
  const { postEditorContext } = useContext(AppContext);
  const { selectedWorkspaceSettings } = useSettings();
  const defaultQueuePosition = selectedWorkspaceSettings?.defaultQueuePosition;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { postGroups } = postEditorState;
  const group = postGroups[postGroupIndex];
  const isBulkImport = postEditorContext.type === PostEditorContextEnum.import;

  const {
    groupPostAttributes: {
      scheduling: { postTiming: globalPostTiming }
    },
    posts,
    errors
  } = group;
  const postTiming =
    customizeIndex !== undefined && posts[customizeIndex]
      ? posts[customizeIndex].scheduling.postTiming
      : globalPostTiming;
  const evergreenType: EvergreenRecycleViewProps['type'] =
    postTiming === PostTiming.RECURRING ? 'recurring' : 'evergreen';
  const customizationProfileId = getCustomizationProfileId(group, customizeIndex);

  const hasError = hasSeverity(errors, ErrorMessageSeverity.error, customizationProfileId, undefined, [
    'checkDateAndTime',
    'checkExpiryCount',
    'checkExpiryDate',
    'checkQueuePosition',
    'expireCountRequired',
    'expireDateRequired',
    'pinterestDuplicatesCheck',
    'preventPostNowForDisconnected',
    'preventTwDuplicate',
    'scheduleTextOrMediaVariations'
  ]);
  const hasWarning =
    !hasError &&
    hasSeverity(errors, ErrorMessageSeverity.warning, customizationProfileId, undefined, ['checkExpiryDate']);

  const boxClasses = classNames(classes.ScheduleBox, {
    [classes.ScheduleBoxError]: hasError,
    [classes.ScheduleBoxWarning]: !hasError && hasWarning,
    [classes.ScheduleBoxBorderless]: borderless
  });

  const boxWrapperClasses = classNames(classes.ScheduleBoxWrapper, {
    [classes.ScheduleBoxWrapperError]: hasError,
    [classes.ScheduleBoxWrapperWarning]: !hasError && hasWarning,
    [classes.ScheduleBoxWrapperBorderless]: borderless,
    [POST_EDITOR_ERROR_CLASS]: hasError
  });

  const isQueue = postTiming === PostTiming.QUEUE;

  return (
    <div className={boxWrapperClasses}>
      <div className={boxClasses}>
        <div className={'grid-x align-middle'}>
          <ButtonGroup size={'medium'}>
            <Button
              disabled={postEditorState.loading || !defaultQueuePosition}
              type={'button'}
              variant={postTiming === PostTiming.QUEUE ? 'solid' : 'hollow'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              action={() =>
                dispatch({
                  type: 'setPostTiming',
                  payload: { postGroupIndex, postTiming: PostTiming.QUEUE, customizeIndex, defaultQueuePosition }
                })
              }>
              Use Posting Plan
            </Button>
            <Button
              disabled={postEditorState.loading}
              type={'button'}
              variant={postTiming !== PostTiming.QUEUE ? 'solid' : 'hollow'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              action={() =>
                dispatch({
                  type: 'setPostTiming',
                  payload: {
                    postGroupIndex,
                    postTiming: isBulkImport ? PostTiming.SCHEDULE : PostTiming.NOW,
                    customizeIndex
                  }
                })
              }>
              Date & Time
            </Button>
          </ButtonGroup>
        </div>

        {isQueue ? (
          <AddToQueue formInstance={formInstance} {...(postId ? { fetchedPostId: postId } : {})} />
        ) : (
          <SchedulePost formInstance={formInstance} />
        )}
        <div className={'grid-x align-bottom'}>
          <div className={'cell shrink'}>
            <EvergreenRecycle formInstance={formInstance} type={evergreenType} />
          </div>
        </div>
      </div>
    </div>
  );
};
